import type { FulfillmentEvent, FulfillmentOrderLine } from '@/infrastructure/FulfillmentAdminClient';
import { kstTimestamp } from '@/utils/fulfillmentTime';
import { cancelledGoodsOf, type CancelledGoodsLine } from './cancelledGoods';

export interface CancelledGoodsNoteProps {
  events: FulfillmentEvent[];
  lines: FulfillmentOrderLine[];
}

const LABEL: Record<CancelledGoodsLine['kind'], string> = {
  KEEP: 'Keep in stock',
  WRITE_OFF: 'Not kept — taken off stock',
  RETURN_TO_SELLER: 'Return to seller',
  NOT_PURCHASED: 'Not purchased - nothing to do',
  NOT_SHIPPED_BY_SELLER: 'Not shipped by seller',
  NOT_RECORDED: 'No instruction recorded',
};

const labelOf = (line: CancelledGoodsLine): string =>
  line.kind === 'WRITE_OFF' && line.returnToSeller ? 'Return to seller - taken off stock' : LABEL[line.kind];

const detailOf = (line: CancelledGoodsLine): string => {
  switch (line.kind) {
    case 'KEEP':
      if (line.location) {
        return `moved${line.qty !== undefined ? ` ${line.qty}` : ''} to ${line.location}`;
      }
      if (line.fromInventory) {
        return 'leave it on the shelf for sale';
      }
      if (line.explicitChoice && !line.arrivalOnRecord) {
        return 'not arrived yet; keep when it arrives';
      }
      return line.arrivalOnRecord
        ? 'leave it on the shelf for sale'
        : 'but no arrival was on record when it was cancelled; if this item arrived after the cancel, check with the order team';
    case 'WRITE_OFF':
      return `${line.qty !== undefined ? `${line.qty} off the book${line.location ? ` at ${line.location}` : ''}; ` : ''}set it aside to return or discard`;
    case 'RETURN_TO_SELLER':
      return line.arrivalOnRecord
        ? 'return requested; no stock removal recorded — check with the order team before returning'
        : 'not arrived yet; return to seller or refuse delivery';
    case 'NOT_PURCHASED':
      return 'no purchase or arrival; no stock movement';
    case 'NOT_SHIPPED_BY_SELLER':
      // [SSD-7906] A cancelled line cannot be inbounded, so goods that turn up anyway have no line to go on.
      return 'no stock movement. If it arrives late, set it aside and ask the order team: a cancelled line cannot be inbounded';
    case 'NOT_RECORDED':
      return 'ask the order team what to do with it';
  }
};

const sourceOf = (line: CancelledGoodsLine): string =>
  [line.by, line.at ? kstTimestamp(line.at) : null, line.reason ? `"${line.reason}"` : null]
    .filter(Boolean)
    .join(' · ');

/**
 * Under a cancelled order: what the cancel record says to do with each line's goods (SSD-7533), so the warehouse
 * no longer has to ask the order team and wait. Each line names the cancel its answer came from.
 */
export function CancelledGoodsNote({ events, lines }: CancelledGoodsNoteProps) {
  const goods = cancelledGoodsOf(events, lines);
  if (goods.length === 0) {
    return null;
  }

  return (
    <div className="cancelled-goods" role="note" aria-label="What to do with the goods">
      <div className="cancelled-goods-head">
        <b>What to do with the goods</b>
      </div>
      <ul>
        {goods.map((line) => {
          const source = sourceOf(line);
          return (
            <li key={line.lineNo} data-testid="cancelled-goods-line" data-kind={line.kind}>
              <span className="num">
                Line {line.lineNo} · {line.sku}
              </span>{' '}
              <span className={`cancelled-goods-kind is-${line.kind.toLowerCase().replace(/_/g, '-')}`}>
                {labelOf(line)}
              </span>{' '}
              <span className="cancelled-goods-detail">— {detailOf(line)}</span>
              {source && <span className="cancelled-goods-who"> ({source})</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
